import {Launch, Visibility} from '@mui/icons-material';
import {Box, Button, Paper, Typography} from '@mui/material';
import useVisualizationParams from '@src/hooks/useVisualizationParams';

const mockGradient = {
  type: 'continuous',
  gradients: [
    {color: '#7c1500', label: '0', position: 0},
    {color: '#c43500', label: '10', position: 0.1},
    {color: '#f17100fa', label: '20', position: 0.2},
    {color: '#ffba48ee', label: '30', position: 0.3},
    {color: '#fff83add', label: '40', position: 0.4},
    {color: '#abfa43dd', label: '50', position: 0.5},
    {color: '#59ff8edd', label: '60', position: 0.6},
    {color: '#96fffbdd', label: '70', position: 0.7},
    {color: '#aad2ffdd', label: '80', position: 0.8},
    {color: '#d5d9ff99', label: '90', position: 0.9},
    {color: 'rgba(255, 255, 255, 0.4)', label: '100', position: 1.0},
  ],
};

export function ConfidenceLegend({showActions}: {showActions?: boolean}) {
  const isSuperRes = useVisualizationParams().isSuperRes;

  if (!isSuperRes) {
    return null;
  }

  const steps = 11;
  const cells = Array.from({length: steps}, (_, i) => {
    const gradient = mockGradient.gradients[i];
    const value = i * 10;

    return {
      position: i / (steps - 1),
      label: value.toString(),
      color: gradient.color,
      labelInfo: gradient.label,
    };
  });

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 2,
        paddingBlockEnd: 2,
      }}
    >
      <Box
        sx={{
          width: '100%',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          marginBlockTop: 2,
        }}
      >
        <Box sx={{width: '100%', position: 'relative'}}>
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'relative',
              height: 29,
              borderRadius: 0,
            }}
          >
            {cells.map(cell => (
              <Typography
                key={`tooltip${cell.position}`}
                variant="caption"
                sx={{
                  textAlign: 'center',
                  flex: 1,
                  color: 'text.secondary',
                  fontSize: '0.85rem',
                  whiteSpace: 'nowrap',
                }}
              >
                {cell.label}
              </Typography>
            ))}
          </Paper>

          <Box sx={{position: 'relative'}}>
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                height: 22,
                backgroundImage: `
              linear-gradient(45deg, #ccc 25%, transparent 25%),
              linear-gradient(-45deg, #ccc 25%, transparent 25%),
              linear-gradient(45deg, transparent 75%, #ccc 75%),
              linear-gradient(-45deg, transparent 75%, #ccc 75%)
            `,
                backgroundSize: '8px 8px',
                backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
              }}
            />

            <Box
              sx={{
                display: 'flex',
                height: 22,
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              {cells.map(cell => (
                <Box
                  key={`cell-${cell.position}`}
                  sx={{
                    flex: 1,
                    position: 'relative',
                    backgroundColor: cell.color,
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>

        {showActions && (
          <Button
            startIcon={<Launch />}
            variant="outlined"
            color="secondary"
            size="small"
            sx={{
              backgroundColor: 'background.paper',
              paddingInline: 1.4,
              minWidth: 0,
              '& .MuiButton-startIcon': {marginInlineEnd: -0.2},
            }}
          />
        )}
      </Box>

      {showActions && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            marginBlockTop: 1,
          }}
        >
          <Button
            startIcon={<Visibility />}
            variant="outlined"
            color="secondary"
            size="small"
            sx={{
              backgroundColor: 'background.paper',
              border: 'none',
              borderRadius: 0.5,
              paddingBlock: 1,
              paddingInline: 1,
              minWidth: 0,
              '& .MuiButton-startIcon': {marginInlineEnd: -0.2},
            }}
          />
          <Typography variant="caption" color="text.secondary">
            Show/Hide Confidence Layer
          </Typography>
        </Box>
      )}
    </Box>
  );
}
